import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { 
  Play, Pause, SkipBack, SkipForward, 
  Repeat, Shuffle, Volume2, VolumeX, 
  ChevronUp, ChevronDown, ListMusic, Gauge, Keyboard
} from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const SPEED_OPTIONS = [
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1, label: '1x' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 2, label: '2x' },
];

const REPEAT_OPTIONS = [
  { value: 0, label: 'معطّل' },
  { value: 1, label: 'مرة واحدة' },
  { value: 10, label: '١٠ مرات' },
  { value: 50, label: '٥٠ مرة' },
  { value: 100, label: '١٠٠ مرة' },
  { value: -1, label: '∞ تكرار لا نهائي' },
];

const SHORTCUTS = [
  { key: 'مسافة', action: 'تشغيل / إيقاف' },
  { key: '←', action: 'تقديم ٥ ثوانٍ' },
  { key: '→', action: 'ترجيع ٥ ثوانٍ' },
  { key: '↑', action: 'رفع الصوت' },
  { key: '↓', action: 'خفض الصوت' },
  { key: 'N', action: 'التالي' },
  { key: 'P', action: 'السابق' },
  { key: 'M', action: 'كتم' },
  { key: 'L', action: 'تكرار' },
  { key: 'S', action: 'عشوائي' },
];

export const AudioPlayer = () => {
  const { t } = useLanguage();
  const {
    currentExcerpt,
    currentSurah,
    isPlaying,
    isShuffle,
    volume,
    currentTime,
    duration,
    isMinimized,
    isPlayingBasmallah,
    playbackSpeed,
    repeatCount,
    currentRepeatIndex,
    togglePlay,
    seek,
    playNext,
    playPrevious,
    setVolume,
    isVolumeSupported,
    setIsShuffle,
    setIsMinimized,
    setPlaybackSpeed,
    setRepeatCount,
  } = usePlayer();

  if (!currentExcerpt) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const displayTitle = currentExcerpt.title || `${t('pageNumber')} ${currentExcerpt.page_number}`;
  
  const getRepeatLabel = () => {
    if (repeatCount === 0) return null;
    if (repeatCount === -1) return '∞';
    return `${currentRepeatIndex + 1}/${repeatCount}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50"
        dir="rtl"
        data-testid="audio-player"
      >
        {isMinimized ? (
          <motion.div 
            className="glass-strong mx-4 mb-4 rounded-2xl overflow-hidden"
            layout
          >
            <div className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <p className="font-arabic text-lg truncate" dir="rtl">
                  {currentSurah?.name_arabic}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {isPlayingBasmallah 
                    ? 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ' 
                    : repeatCount !== 0 
                      ? `${displayTitle}  ${getRepeatLabel()}`
                      : displayTitle
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                {repeatCount !== 0 && (
                  <span className="text-xs font-bold bg-primary text-primary-foreground rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5" data-testid="player-repeat-counter-mini">
                    {getRepeatLabel()}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={togglePlay}
                  className="rounded-full"
                  data-testid="player-play-btn"
                >
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMinimized(false)}
                  className="rounded-full"
                  data-testid="player-expand-btn"
                >
                  <ChevronUp className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="h-1 bg-muted">
              <div 
                className="h-full bg-primary transition-all duration-150 mr-auto"
                style={{ width: `${progress}%`, marginRight: 0, marginLeft: 'auto' }}
              />
            </div>
          </motion.div>
        ) : (
          <motion.div 
            className="glass-strong rounded-t-3xl overflow-hidden"
            layout
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <ListMusic className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('nowPlaying')}</p>
                  <p className="font-arabic text-lg" dir="rtl">
                    {currentSurah?.name_arabic}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMinimized(true)}
                className="rounded-full"
                data-testid="player-minimize-btn"
              >
                <ChevronDown className="h-5 w-5" />
              </Button>
            </div>

            {/* Main Content */}
            <div className="p-6 space-y-6">
              {/* Track Info */}
              <div className="text-center">
                <h3 className="font-arabic text-3xl mb-2" dir="rtl">
                  {currentSurah?.name_arabic}
                </h3>
                <p className="text-muted-foreground">
                  {isPlayingBasmallah 
                    ? 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ' 
                    : displayTitle
                  }
                </p>
              </div>

              {/* Progress */}
              <div className="space-y-2">
                <Slider
                  value={[currentTime]}
                  max={duration || 100}
                  step={0.1}
                  onValueChange={([value]) => seek(value)}
                  className="cursor-pointer"
                  dir="rtl"
                  data-testid="player-progress-slider"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatTime(duration)}</span>
                  <span>{formatTime(currentTime)}</span>
                </div>
              </div>

              {/* Main Controls */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsShuffle(!isShuffle)}
                  className={`rounded-full ${isShuffle ? 'text-primary' : 'text-muted-foreground'}`}
                  data-testid="player-shuffle-btn"
                >
                  <Shuffle className="h-5 w-5" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={playNext}
                  className="rounded-full h-12 w-12"
                  data-testid="player-next-btn"
                >
                  <SkipForward className="h-6 w-6" />
                </Button>

                <Button
                  onClick={togglePlay}
                  className="rounded-full h-16 w-16 bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="player-play-main-btn"
                >
                  {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={playPrevious}
                  className="rounded-full h-12 w-12"
                  data-testid="player-prev-btn"
                >
                  <SkipBack className="h-6 w-6" />
                </Button>

                {/* Repeat Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`rounded-full relative ${repeatCount !== 0 ? 'text-primary' : 'text-muted-foreground'}`}
                      data-testid="player-loop-btn"
                    >
                      <Repeat className="h-5 w-5" />
                      {repeatCount !== 0 && (
                        <span className="absolute -top-2 -right-2 text-[11px] font-bold bg-primary text-primary-foreground rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                          {getRepeatLabel()}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    {REPEAT_OPTIONS.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => setRepeatCount(option.value)}
                        className={repeatCount === option.value ? 'bg-primary/10 text-primary' : ''}
                        data-testid={`repeat-${option.value}`}
                      >
                        {option.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Secondary Controls */}
              <div className="flex items-center justify-between">
                {/* Volume control */}
                {isVolumeSupported ? (
                  <div className="hidden sm:flex items-center gap-3 w-32" dir="ltr" data-testid="player-volume-control">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setVolume(volume > 0 ? 0 : 0.8)}
                      className="rounded-full h-8 w-8"
                      data-testid="player-mute-btn"
                    >
                      {volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                    </Button>
                    <Slider
                      value={[volume * 100]}
                      max={100}
                      step={1}
                      onValueChange={([value]) => setVolume(value / 100)}
                      className="cursor-pointer"
                      data-testid="player-volume-slider"
                    />
                  </div>
                ) : (
                  // iOS: hardware volume only. Show a clear hint to use device buttons.
                  <div
                    className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground"
                    dir="rtl"
                    data-testid="player-volume-ios-hint"
                    title="على iPhone/iPad، استخدم أزرار الصوت الجانبية"
                  >
                    <Volume2 className="h-4 w-4 opacity-70" />
                    <span>استخدم أزرار الصوت بالجهاز</span>
                  </div>
                )}
                {/* Spacer on mobile */}
                <div className="sm:hidden" />

                {/* Playback Speed */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`rounded-full gap-1 text-sm ${playbackSpeed !== 1 ? 'text-primary' : 'text-muted-foreground'}`}
                      data-testid="player-speed-btn"
                    >
                      <Gauge className="h-4 w-4" />
                      <span>{playbackSpeed}x</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    {SPEED_OPTIONS.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => setPlaybackSpeed(option.value)}
                        className={playbackSpeed === option.value ? 'bg-primary/10 text-primary' : ''}
                        data-testid={`speed-${option.value}`}
                      >
                        {option.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Keyboard Shortcuts - Desktop only */}
                <div className="hidden md:block">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full h-8 w-8 text-muted-foreground hover:text-foreground"
                          data-testid="player-shortcuts-btn"
                        >
                          <Keyboard className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="p-3 max-w-xs">
                        <p className="font-semibold mb-2 text-sm">اختصارات لوحة المفاتيح</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {SHORTCUTS.map((shortcut) => (
                            <div key={shortcut.key} className="flex justify-between gap-2">
                              <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">{shortcut.key}</kbd>
                              <span className="text-muted-foreground">{shortcut.action}</span>
                            </div>
                          ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
